import { useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { getSupabase } from '../utils/supabaseClient';

const BACKEND = (process.env.REACT_APP_BACKEND || 'local').toLowerCase();

const describeSession = (session: Session | null): { hasSession: boolean; expiresAt: string | null } => ({
  hasSession: session !== null,
  expiresAt: session?.expires_at
    ? new Date(session.expires_at * 1000).toISOString()
    : null,
});

export function useSupabaseSession(): { session: Session | null; loading: boolean } {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState<boolean>(BACKEND === 'supabase');

  useEffect(() => {
    if (BACKEND !== 'supabase') {
      setSession(null);
      setLoading(false);
      return;
    }

    const supabase = getSupabase();
    let alive = true;

    supabase.auth.getSession().then(({ data, error }) => {
      if (!alive) return;
      if (error) {
        console.error('[Auth] 保存済みセッションの読み出しに失敗しました:', error);
        setSession(null);
        setLoading(false);
        return;
      }
      console.info('[Auth] 初期セッションを読み込みました:', describeSession(data.session));
      setSession(data.session ?? null);
      setLoading(false);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!alive) return;
      if (event === 'SIGNED_OUT') {
        console.warn('[Auth] SIGNED_OUT を受信しました。Supabase がセッションを無効と判断しています。');
      } else {
        console.info(`[Auth] ${event} を受信しました:`, describeSession(nextSession));
      }
      setSession(nextSession);
    });

    return () => {
      alive = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  return { session, loading };
}
